// createEstra(): the Worker handler plus two in-process clients over one app's D1:
//   admin(env)          — skips rules (service key, server code, triggers),
//   client(env, auth)   — every read and write checked against the app's RuleSet.
import { readJwtClaims } from "@estra/edge-core";
import { fieldAt, schemaToJSON, type CommitRequest, type DocOut, type Query, type Schema, type Write } from "@estra/schema";
import { afterImage } from "./after.ts";
import { consoleRoute } from "./console.ts";
import { commit, withoutIdEcho, type CommitOpts, type CommitResult, type WriteImage } from "./commit.ts";
import { bad, EstraError, STATUS } from "./errors.ts";
import { docRef } from "./paths.ts";
import { aggregateDocs, getDocs, refFor, runAggregate, runQuery, type QueryResult } from "./read.ts";
import type { EstraPlugin, PluginApi } from "./plugin.ts";
import { notifyTopics, realtimeClasses, type RealtimeConfig } from "./realtime.ts";
import { CHANNEL_NAME, channelClasses, type ChannelsConfig } from "./channels.ts";
import { counterClasses, type CountersConfig } from "./counters.ts";
import { drainEvents, eventTables, maintenance, type DrainOpts, type TriggerSet } from "./triggers.ts";
import { LOOKUP_LIMIT, toRuleDoc, type Lookup, type Method, type RuleAuth, type RuleDoc, type RuleSet } from "./rules.ts";
import { collectionOf, MAX_POINTS, routeClass, statusClass, usageWriter, type UsageConfig, type UsageWriter } from "./usage.ts";

export interface EstraConfig<Env> {
  schema: Schema;
  /** The app's D1 binding. */
  db: (env: Env) => D1Database;
  /** Service key for admin callers (`Authorization: Bearer <key>`); unset = no HTTP admin access. */
  serviceKey?: (env: Env) => string | undefined;
  /** Turns a bearer token into the caller (null = bad token → 401). No header = signed out. */
  authenticate?: (token: string, env: Env) => Promise<RuleAuth | null>;
  /** Security rules for non-admin callers. Without rules, only admins get in. */
  rules?: RuleSet;
  /** Largest page a query may return (default 1000). */
  maxLimit?: number;
  /**
   * Browser apps on other origins: which origins may call the HTTP API (exact matches such as
   * "https://app.example.com", or "*"). Preflights are answered and responses carry the CORS
   * headers. Tokens travel in the Authorization header, so no credentials mode is needed.
   * Unset: no CORS headers (same-origin, server and mobile callers only).
   */
  cors?: { origins: string[] | "*"; maxAgeS?: number };
  /** Rows a near query may scan before its exact distance filter (default 5000). */
  nearScanCap?: number;
  /** Rows a client-side (rule-checked) aggregate may read (default 5000). */
  clientAggregateCap?: number;
  /** Plugins (auth, storage …): their routes, tables and token verifiers. */
  plugins?: EstraPlugin<Env>[];
  /** Server triggers (defineTriggers). Their tables' writes go to the _events outbox. */
  triggers?: TriggerSet<Env>;
  /** Queue that wakes the trigger drainer (else drained in waitUntil). Bind its consumer to estra.queue. */
  queue?: (env: Env) => Queue;
  /** Realtime listeners (Durable Object namespaces for `estra.realtime()`'s classes). */
  realtime?: RealtimeConfig<Env>;
  /** Ephemeral channels: presence and broadcast at /v1/channels/<name> (`estra.channels()`'s class). */
  channels?: ChannelsConfig<Env>;
  /** Hot counters: buffered increments at /v1/counters/inc (`estra.counters()`'s class). */
  counters?: CountersConfig<Env>;
  /** Runs after every successful commit (realtime fan-out and triggers hook in here). */
  afterCommit?: (result: CommitResult, env: Env, ctx: ExecutionContext | undefined) => void | Promise<void>;
  /**
   * Usage points to Workers Analytics Engine (usage.ts): one per request, per extra collection a
   * commit touches, and per realtime open / close / snapshot flush. Counts and names only.
   */
  usage?: UsageConfig<Env>;
  /**
   * The project console's routes (console.ts) at /v1/console/*, for an app's own Worker attached to
   * Estra Cloud (hostedEstra tenants have them already). Only these keys are taken, never the
   * service key. `rules`/`storageRules` are the texts GET /v1/console/config shows.
   */
  console?: { keys: (env: Env) => string | undefined | (string | undefined)[]; rules?: string; storageRules?: string };
}

export interface Client {
  get(path: string): Promise<DocOut | null>;
  getAll(paths: string[]): Promise<(DocOut | null)[]>;
  query(query: Query): Promise<QueryResult>;
  aggregate(query: Query): Promise<Record<string, number | null>>;
  commit(req: CommitRequest, opts?: CommitOpts): Promise<CommitResult>;
  /** Would the rules let this caller get this document as it is? (Realtime checks images in memory.) */
  canGet?(doc: DocOut): Promise<boolean>;
  /** Raw rules check for non-document paths (channels, counters …): true when allowed. */
  allows?(method: Method, path: string, resource: RuleDoc | null, after: RuleDoc | null): Promise<boolean>;
}
/** @deprecated name kept for H1 callers. */
export type AdminClient = Client;

const MAX_BODY = 1 << 20;
/** The frozen HTTP/WebSocket contract (docs/api-v1.md). Breaking changes need /v2. */
export const API_VERSION = 1;
export const API_HEADER = "x-estra-api";
const COMMIT_RETRIES = 3;
const enc = new TextEncoder();

/** Writes past this many in one commit aren't looked at for usage (the collections are named by the first ones). */
const MAX_WRITES_METERED = 500;

/** What one request did, for its usage points: the route class and the docs per collection. */
class Meter {
  route: string | undefined;
  cols = new Map<string, { docs: number; deletes: number }>();
  add(name: string | null, docs: number, deletes: number) {
    const k = name ?? "";
    const c = this.cols.get(k);
    if (c) {
      c.docs += docs;
      c.deletes += deletes;
    } else this.cols.set(k, { docs, deletes });
  }
  /** One point per request (double1 = 1); a commit adds one per further collection with double1 = 0. */
  write(w: UsageWriter, req: Request, status: number, ms: number) {
    const rc = routeClass(new URL(req.url).pathname, req.method);
    const country = (req as { cf?: { country?: unknown } }).cf?.country;
    const base = { route: this.route ?? rc.route, status: statusClass(status), ...(typeof country === "string" ? { country } : {}) };
    const cols = [...this.cols].slice(0, MAX_POINTS);
    const [first, ...rest] = cols;
    w({ ...base, name: first?.[0] || rc.name || "", count: 1, ms, docs: first?.[1].docs ?? 0, deletes: first?.[1].deletes ?? 0 });
    for (const [name, c] of rest) w({ ...base, name, count: 0, ms: 0, docs: c.docs, deletes: c.deletes });
  }
}

export async function sameSecret(a: string, b: string): Promise<boolean> {
  // Compare digests (equal length) in constant time; workerd's timingSafeEqual isn't in Node.
  const [x, y] = (await Promise.all([crypto.subtle.digest("SHA-256", enc.encode(a)), crypto.subtle.digest("SHA-256", enc.encode(b))])).map((d) => new Uint8Array(d));
  let diff = 0;
  for (let i = 0; i < x!.length; i++) diff |= x![i]! ^ y![i]!;
  return diff === 0;
}

export async function readJson(req: Request): Promise<Record<string, unknown>> {
  const len = Number(req.headers.get("content-length") ?? 0);
  if (len > MAX_BODY) throw new EstraError("resource-exhausted", "body over 1 MiB");
  const text = await req.text();
  if (text.length > MAX_BODY) throw new EstraError("resource-exhausted", "body over 1 MiB");
  try {
    const v = JSON.parse(text) as unknown;
    if (typeof v !== "object" || v === null || Array.isArray(v)) throw new Error();
    return v as Record<string, unknown>;
  } catch {
    return bad("body must be a JSON object");
  }
}

const json = (body: unknown, status = 200) => new Response(JSON.stringify(body), { status, headers: { "content-type": "application/json" } });
const denied = (method: Method, path: string) => new EstraError("permission-denied", `${method} ${path} denied by rules`);

/** Verifies HS256 access tokens (edge-core) as the caller: `sub` → uid, all claims → token. */
export function hs256Auth<Env>(o: { secret: (env: Env) => string; audience: string; issuer?: string }) {
  return async (token: string, env: Env): Promise<RuleAuth | null> => {
    const c = await readJwtClaims(o.secret(env), token, o.audience, Date.now(), o.issuer);
    return c ? { uid: c.sub, token: c } : null;
  };
}

export function createEstra<Env>(cfg: EstraConfig<Env>) {
  const opts = { maxLimit: cfg.maxLimit ?? 1000, nearScanCap: cfg.nearScanCap ?? 5000 };
  const aggCap = cfg.clientAggregateCap ?? 5000;

  const evTables = eventTables(cfg.triggers);
  const afterCommit = async (r: CommitResult, env: Env, ctx?: ExecutionContext, images?: WriteImage[]) => {
    if (r.duplicate || (!cfg.afterCommit && !cfg.realtime && !r.events)) return;
    const p = Promise.all([
      r.events ? wakeTriggers(env, ctx) : null,
      cfg.realtime ? notifyTopics(cfg.realtime, env, cfg.schema, r.writes.map((w) => w.path), images) : null,
      cfg.afterCommit ? cfg.afterCommit(r, env, ctx) : null,
    ]);
    if (ctx) ctx.waitUntil(p);
    else await p;
  };

  const admin = (env: Env, ctx?: ExecutionContext): Client => {
    const db = cfg.db(env);
    return {
      get: async (path) => (await getDocs(db, cfg.schema, [path]))[0] ?? null,
      getAll: (paths) => getDocs(db, cfg.schema, paths),
      canGet: async () => true,
      allows: async () => true,
      query: (query) => runQuery(db, cfg.schema, query, opts),
      aggregate: (query) => runAggregate(db, cfg.schema, query, opts),
      commit: async (req, o) => {
        let images: WriteImage[] | undefined;
        const r = await commit(db, cfg.schema, req, { ...o, eventTables: evTables, ...(cfg.realtime ? { onImages: (i: WriteImage[]) => (images = i) } : {}) });
        await afterCommit(r, env, ctx, images);
        return r;
      },
    };
  };

  /** A rule-checked client for one caller (auth null = signed out). */
  const client = (env: Env, auth: RuleAuth | null, ctx?: ExecutionContext): Client => {
    const db = cfg.db(env);
    const rules = cfg.rules;
    const lookup = (limit: number, overlay?: Map<string, RuleDoc | null>): Lookup => {
      const memo = new Map<string, Promise<RuleDoc | null>>();
      const get = (path: string) => {
        let p = memo.get(path);
        if (!p) {
          if (memo.size >= limit) throw new Error(`more than ${limit} get()/exists() lookups`);
          docRef(cfg.schema, path);
          p = getDocs(db, cfg.schema, [path]).then((d) => toRuleDoc(d[0] ?? null));
          memo.set(path, p);
        }
        return p;
      };
      return { get, getAfter: async (path) => (overlay?.has(path) ? overlay.get(path)! : get(path)) };
    };
    const check = async (method: Method, path: string, resource: RuleDoc | null, after: RuleDoc | null, lk: Lookup, now: number, query?: { limit?: number }) => {
      if (!rules || !(await rules.allow({ method, path, auth, resource, after, time: now, lookup: lk, ...(query ? { query } : {}) }))) throw denied(method, path);
    };
    const checkRows = async (docs: DocOut[], limit?: number) => {
      const lk = lookup(LOOKUP_LIMIT.read);
      const now = Date.now();
      for (const d of docs) await check("list", d.path, toRuleDoc(d), null, lk, now, { limit });
    };
    /** true = allowed as a whole (no per-row checks); false = refused; undefined = check rows. */
    const decideQuery = async (query: Query): Promise<boolean | undefined> => {
      if (!rules) throw new EstraError("permission-denied", "no rules");
      if (!rules.allowQuery) return undefined;
      const ref = refFor(cfg.schema, query);
      const d = await rules.allowQuery({
        collection: ref.group ? query.group! : ref.path!,
        group: ref.group,
        ...(query.where ? { where: query.where } : {}),
        ...((query.limit ?? query.limitToLast) !== undefined ? { limit: query.limit ?? query.limitToLast } : {}),
        auth,
        time: Date.now(),
        lookup: lookup(LOOKUP_LIMIT.read),
      });
      if (d === false) throw new EstraError("permission-denied", `list ${ref.path ?? query.group} denied by rules`);
      return d;
    };

    return {
      async allows(method, path, resource, after) {
        try {
          await check(method, path, resource, after, lookup(method === "get" || method === "list" ? LOOKUP_LIMIT.read : LOOKUP_LIMIT.write), Date.now());
          return true;
        } catch (e) {
          if (e instanceof EstraError && e.code === "permission-denied") return false;
          throw e;
        }
      },
      async canGet(doc) {
        try {
          await check("get", doc.path, toRuleDoc(doc), null, lookup(LOOKUP_LIMIT.read), Date.now());
          return true;
        } catch (e) {
          if (e instanceof EstraError && e.code === "permission-denied") return false;
          throw e;
        }
      },
      get: async (path) => (await client(env, auth, ctx).getAll([path]))[0] ?? null,
      async getAll(paths) {
        const docs = await getDocs(db, cfg.schema, paths);
        const lk = lookup(LOOKUP_LIMIT.read);
        const now = Date.now();
        for (let i = 0; i < paths.length; i++) await check("get", docRef(cfg.schema, paths[i]!).path, toRuleDoc(docs[i] ?? null), null, lk, now);
        return docs;
      },
      async query(query) {
        if (query.offset) bad("offset needs the service key; page with cursors (startAfter)");
        const decided = await decideQuery(query);
        const r = await runQuery(db, cfg.schema, query, opts);
        if (decided === undefined) await checkRows(r.docs, query.limit ?? query.limitToLast);
        return r;
      },
      async aggregate(query) {
        if (!query.aggregate) bad("aggregate missing");
        if (query.limitToLast !== undefined || query.offset) bad("aggregates can't use limitToLast or offset");
        // Allowed as a whole: count in SQL, no rows read.
        if ((await decideQuery(query)) === true) return runAggregate(db, cfg.schema, query, opts);
        // Rules run per document, so a client count reads the rows (up to clientAggregateCap).
        const limit = query.limit ?? aggCap;
        const r = await runQuery(db, cfg.schema, { ...query, aggregate: undefined, limit }, { ...opts, maxLimit: Math.max(aggCap, opts.maxLimit) });
        if (r.hasMore && query.limit === undefined) throw new EstraError("resource-exhausted", `client aggregates read at most ${aggCap} documents; set a limit or count on the server`);
        await checkRows(r.docs, query.limit);
        return aggregateDocs(r.docs, query.aggregate!);
      },
      async commit(req, o) {
        const writes = withoutIdEcho(req?.writes);
        if (!Array.isArray(writes) || !writes.length) bad("commit needs writes");
        if (o?.origin) bad("origin is for server writes");
        const refs = writes.map((w) => docRef(cfg.schema, w.path));
        for (let attempt = 1; ; attempt++) {
          const now = Date.now();
          const befores = await getDocs(db, cfg.schema, refs.map((r) => r.path));
          const overlay = new Map<string, RuleDoc | null>();
          // A document written more than once: each write starts from the previous one's result.
          const ruleBefores: (RuleDoc | null)[] = [];
          const afters = writes.map((w, i) => {
            const path = refs[i]!.path;
            const prev = overlay.has(path) ? overlay.get(path)! : toRuleDoc(befores[i] ?? null);
            ruleBefores.push(prev);
            const data = afterImage(refs[i]!.table, prev ? (prev.data as Record<string, unknown>) : null, w, now);
            const doc = data ? { path, id: refs[i]!.id, data } : null;
            overlay.set(path, doc);
            return doc;
          });
          const lk = lookup(LOOKUP_LIMIT.write, overlay);
          for (let i = 0; i < writes.length; i++) {
            const w = writes[i]!;
            const before = ruleBefores[i]!;
            // A check reveals a document's version, so it needs read access.
            const method: Method = w.op === "check" ? "get" : w.op === "delete" ? "delete" : w.op === "update" || before ? "update" : "create";
            await check(method, refs[i]!.path, before, w.op === "check" ? null : afters[i]!, lk, now);
          }
          // Guard every write on the version the rules saw; a racing write → re-check and retry.
          // (Only a document's first write: later ones in the batch see the version it leaves.)
          const firstAt = new Map<string, number>();
          refs.forEach((r, i) => firstAt.has(r.path) || firstAt.set(r.path, i));
          const guarded: Write[] = writes.map((w, i) => (w.pre?.version !== undefined || firstAt.get(refs[i]!.path) !== i ? w : { ...w, pre: { ...w.pre, version: befores[i]?.v ?? 0 } }));
          try {
            let images: WriteImage[] | undefined;
            const r = await commit(db, cfg.schema, { ...req, writes: guarded }, { now, eventTables: evTables, ...(cfg.realtime ? { onImages: (i: WriteImage[]) => (images = i) } : {}) });
            await afterCommit(r, env, ctx, images);
            return r;
          } catch (e) {
            const raced = e instanceof EstraError && e.code === "failed-precondition" && writes.every((w) => w.pre?.version === undefined);
            if (!raced || attempt >= COMMIT_RETRIES) throw raced ? new EstraError("aborted", "documents kept changing; retry") : e;
          }
        }
      },
    };
  };

  const plugins = cfg.plugins ?? [];
  const authenticate = cfg.authenticate ?? plugins.find((p) => p.authenticate)?.authenticate?.bind(undefined);
  const isAdmin = async (req: Request, env: Env) => {
    const h = req.headers.get("authorization");
    const key = cfg.serviceKey?.(env);
    return !!(h?.startsWith("Bearer ") && key && (await sameSecret(h.slice(7), key)));
  };
  const consoleKey = async (req: Request, env: Env) => {
    const h = req.headers.get("authorization");
    if (!h?.startsWith("Bearer ")) return false;
    for (const key of [cfg.console?.keys(env)].flat()) if (key && (await sameSecret(h.slice(7), key))) return true;
    return false;
  };
  // An attached app deploys its own schema and rules, so its configuration has no version.
  const consoleConfig = () =>
    new Response(JSON.stringify({ version: null, updated: null, schema: schemaToJSON(cfg.schema), rules: cfg.console?.rules ?? null, storageRules: cfg.console?.storageRules ?? null, settings: {} }), { headers: { "content-type": "application/json", "cache-control": "no-store" } });
  const pluginEvents = new Map(plugins.flatMap((p) => (p.events ? [[p.events.tbl, p.events.run.bind(p.events)] as const] : [])));
  for (const t of pluginEvents.keys()) if (!t.startsWith("_")) throw new Error(`plugin event table "${t}" must start with "_"`);
  const emit: PluginApi<Env>["emit"] = async (env, ctx, ev) => {
    await cfg
      .db(env)
      .prepare(`INSERT INTO "_events" ("tbl", "path", "op", "before", "after", "origin", "at") VALUES (?1, ?2, ?3, ?4, ?5, ?6, ?7)`)
      .bind(ev.tbl, ev.path, ev.op, ev.before == null ? null : JSON.stringify(ev.before), ev.after == null ? null : JSON.stringify(ev.after), ev.origin ?? null, Date.now())
      .run();
    await wakeTriggers(env, ctx);
  };
  const api: PluginApi<Env> = { schema: cfg.schema, admin, client, db: cfg.db, isAdmin, authenticate: async (t, env) => (authenticate ? authenticate(t, env) : null), emit };

  async function caller(req: Request, env: Env): Promise<{ admin: true } | { admin: false; auth: RuleAuth | null }> {
    const h = req.headers.get("authorization");
    if (!h) return { admin: false, auth: null };
    if (!h.startsWith("Bearer ")) throw new EstraError("unauthenticated", "use Authorization: Bearer <token>");
    const token = h.slice(7);
    const key = cfg.serviceKey?.(env);
    if (key && (await sameSecret(token, key))) return { admin: true };
    const auth = authenticate ? await authenticate(token, env) : null;
    if (!auth) throw new EstraError("unauthenticated", "missing or bad credentials");
    return { admin: false, auth };
  }

  /** The request's Origin when cors allows it (null: no CORS headers). */
  const corsOrigin = (req: Request): string | null => {
    const origin = req.headers.get("origin");
    if (!cfg.cors || !origin) return null;
    return cfg.cors.origins === "*" || cfg.cors.origins.includes(origin) ? origin : null;
  };

  async function fetch(req: Request, env: Env, ctx?: ExecutionContext): Promise<Response> {
    const origin = corsOrigin(req);
    // CORS preflight (https://fetch.spec.whatwg.org/#http-cors-protocol).
    if (req.method === "OPTIONS" && req.headers.get("access-control-request-method")) {
      if (!origin) return new Response(null, { status: 403 });
      return new Response(null, {
        status: 204,
        headers: {
          "access-control-allow-origin": origin,
          "access-control-allow-methods": "GET, POST, PUT, PATCH, DELETE, OPTIONS",
          "access-control-allow-headers": req.headers.get("access-control-request-headers") ?? "authorization, content-type",
          "access-control-max-age": String(cfg.cors?.maxAgeS ?? 86400),
          vary: "Origin",
        },
      });
    }
    const usage = usageWriter(cfg.usage, env);
    const t0 = Date.now();
    const m = usage ? new Meter() : undefined;
    const res = await route(req, env, ctx, m);
    if (usage && m) m.write(usage, req, res.status, Date.now() - t0);
    // WebSocket upgrades are passed through untouched.
    if (res.status === 101) return res;
    const tagged = !!res.headers.get(API_HEADER);
    if (tagged && !origin) return res;
    const out = new Response(res.body, res);
    // Every HTTP answer names the API version.
    if (!tagged) out.headers.set(API_HEADER, String(API_VERSION));
    if (origin) {
      out.headers.set("access-control-allow-origin", origin);
      out.headers.set("access-control-expose-headers", API_HEADER);
      out.headers.append("vary", "Origin");
    }
    return out;
  }

  async function route(req: Request, env: Env, ctx?: ExecutionContext, m?: Meter): Promise<Response> {
    try {
      const url = new URL(req.url);
      if (url.pathname === "/v1/health") return json({ ok: true });
      if (cfg.console && url.pathname.startsWith("/v1/console/")) {
        if (!(await consoleKey(req, env))) throw new EstraError("unauthenticated", "the console routes need the control key");
        return await consoleRoute(req, url, env, ctx, { db: cfg.db(env), app: self, config: async () => consoleConfig() });
      }
      if (url.pathname === "/v1/listen") {
        if (!cfg.realtime) throw new EstraError("not-found", "realtime is off");
        if (req.headers.get("upgrade")?.toLowerCase() !== "websocket") throw new EstraError("invalid-argument", "/v1/listen needs a WebSocket upgrade");
        const ns = cfg.realtime.session(env);
        return ns.get(ns.newUniqueId()).fetch(req);
      }
      const ch = /^\/v1\/channels\/([^/]+)$/.exec(url.pathname);
      if (ch) {
        if (!cfg.channels) throw new EstraError("not-found", "channels are off");
        const name = decodeURIComponent(ch[1]!);
        if (!CHANNEL_NAME.test(name)) throw new EstraError("invalid-argument", "channel names are 1–128 of [A-Za-z0-9_:.-]");
        if (req.headers.get("upgrade")?.toLowerCase() !== "websocket") throw new EstraError("invalid-argument", "/v1/channels/<name> needs a WebSocket upgrade");
        const ns = cfg.channels.namespace(env);
        const fwd = new Request(req);
        fwd.headers.set("x-estra-channel", name);
        return ns.get(ns.idFromName(name)).fetch(fwd);
      }
      for (const p of plugins) {
        const r = p.handle ? await p.handle(req, url, env, ctx, api) : null;
        if (r) return r;
      }
      if (req.method !== "POST") throw new EstraError("not-found", "unknown route");
      const who = await caller(req, env);
      const c = who.admin ? admin(env, ctx) : client(env, who.auth, ctx);
      const body = await readJson(req);
      switch (url.pathname) {
        case "/v1/counters/inc": {
          if (!cfg.counters) throw new EstraError("not-found", "counters are off");
          const { path, field, by } = body as { path?: unknown; field?: unknown; by?: unknown };
          if (typeof path !== "string" || typeof field !== "string") bad("counters/inc needs path, field and by");
          const ref = docRef(cfg.schema, path as string);
          const at = fieldAt(ref.table.fields, field as string);
          const numeric = at && (at.rest.length ? at.fd.kind === "json" : at.fd.kind === "int" || at.fd.kind === "float");
          if (!numeric) bad(`${ref.table.table}.${field}: counters need a number field`);
          if (typeof by !== "number" || !Number.isFinite(by) || (at!.fd.kind === "int" && !Number.isSafeInteger(by))) bad("by must be a finite number (an integer for int fields)");
          const ns = cfg.counters.namespace(env);
          const r = await ns.get(ns.idFromName(ref.path)).fetch("https://estra/inc", { method: "POST", body: JSON.stringify({ path: ref.path, field, by, auth: who.admin ? null : who.auth, admin: who.admin }) });
          return new Response(r.body, { status: r.status, headers: { "content-type": "application/json", "cache-control": "no-store" } });
        }
        case "/v1/get": {
          const docs = await c.getAll(body.paths as string[]);
          if (m) for (const d of docs) if (d) m.add(collectionOf(cfg.schema, d.path), 1, 0);
          return json({ docs });
        }
        case "/v1/query": {
          const query = body.query as Query;
          if (typeof query !== "object" || query === null) bad("query missing");
          const name = typeof query.from === "string" ? collectionOf(cfg.schema, query.from) : typeof query.group === "string" ? query.group : null;
          if (query.aggregate) {
            if (m) m.route = "agg";
            m?.add(name, 1, 0);
            return json({ aggregate: await c.aggregate(query) });
          }
          m?.add(name, 0, 0);
          const r = await c.query(query);
          m?.add(name, r.docs.length, 0);
          return json(r);
        }
        case "/v1/commit": {
          // Counted from the request, so a refused commit still says which collections it tried (status tells).
          const ws = Array.isArray(body.writes) ? (body.writes as Write[]) : [];
          if (m) for (const w of ws.slice(0, MAX_WRITES_METERED)) if (w && w.op !== "check") m.add(collectionOf(cfg.schema, w.path), w.op === "delete" ? 0 : 1, w.op === "delete" ? 1 : 0);
          return json(await c.commit(body as unknown as CommitRequest));
        }
        default:
          throw new EstraError("not-found", "unknown route");
      }
    } catch (e) {
      if (e instanceof EstraError) return json({ error: { code: e.code, message: e.message } }, STATUS[e.code]);
      console.error("estra: internal error", e instanceof Error ? e.message : e);
      return json({ error: { code: "internal", message: "internal error" } }, 500);
    }
  }

  /** Runs pending trigger events (also safe to call from a cron as a sweeper). */
  const drain = (env: Env, o?: DrainOpts) => {
    if (!cfg.triggers && !pluginEvents.size) return Promise.resolve({ done: 0, failed: 0, dead: 0 });
    const a = admin(env);
    return drainEvents(cfg.db(env), cfg.schema, cfg.triggers, env, a, (req, co) => a.commit(req, co), o, pluginEvents);
  };
  async function wakeTriggers(env: Env, ctx?: ExecutionContext) {
    const q = cfg.queue?.(env);
    if (q) await q.send({ t: "estra.events" });
    else {
      const p = drain(env);
      if (ctx) ctx.waitUntil(p);
      else await p;
    }
  }
  /** Queue consumer: drain until empty (bounded), then ack. */
  const queue = async (batch: MessageBatch, env: Env) => {
    for (let i = 0; i < 10; i++) {
      const r = await drain(env);
      if (!r.done && !r.failed && !r.dead) break;
    }
    batch.ackAll();
  };
  /** Cron: sweep undelivered events and prune system tables. */
  const scheduled = async (_c: ScheduledController | null, env: Env) => {
    await drain(env);
    await maintenance(cfg.db(env));
  };

  /** Durable Object classes for listeners: export them from the Worker and bind them in `realtime`. */
  const realtime = () => {
    if (!cfg.realtime) throw new Error("createEstra({ realtime }) is not set");
    return realtimeClasses<Env>({ schema: cfg.schema, client: (env, auth) => client(env, auth), admin: (env) => admin(env), ...(authenticate ? { authenticate } : {}), ...(cfg.usage ? { usage: cfg.usage } : {}), cfg: cfg.realtime });
  };

  // `api` is what plugins get, for calling plugin helpers from app code (e.g. storage.sweep(env, estra.api)).
  /** The Durable Object class for channels: export it from the Worker and bind it in `channels`. */
  const channels = () => {
    if (!cfg.channels) throw new Error("createEstra({ channels }) is not set");
    return channelClasses<Env>({ client: (env, auth) => client(env, auth), admin: (env) => admin(env), ...(authenticate ? { authenticate } : {}), cfg: cfg.channels });
  };

  /** The Durable Object class for hot counters: export it and bind it in `counters`. */
  const counters = () => {
    if (!cfg.counters) throw new Error("createEstra({ counters }) is not set");
    return counterClasses<Env>({ client: (env, auth) => client(env, auth), admin: (env) => admin(env), cfg: cfg.counters });
  };

  const self = { fetch, admin, client, realtime, channels, counters, drain, queue, scheduled, api, plugins: plugins as readonly EstraPlugin<Env>[], schema: cfg.schema, rules: cfg.rules };
  return self;
}

/** What createEstra() returns. */
export type Estra<Env> = ReturnType<typeof createEstra<Env>>;
